/**
 * Business details an owner gives once (address, phone, opening hours...). They become schema.org LocalBusiness
 * markup on the published page, which is what search engines read for local results and maps.
 */
export const DAY_CODES = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
export const DAY_NAMES = {
    Mo: 'Monday',
    Tu: 'Tuesday',
    We: 'Wednesday',
    Th: 'Thursday',
    Fr: 'Friday',
    Sa: 'Saturday',
    Su: 'Sunday'
};
/** Common local-business kinds, in the owner's words, mapped to schema.org types. */
export const BUSINESS_TYPES = [
    { id: 'LocalBusiness', label: 'Other local business' },
    { id: 'Bakery', label: 'Bakery' },
    { id: 'CafeOrCoffeeShop', label: 'Café or coffee shop' },
    { id: 'Restaurant', label: 'Restaurant' },
    { id: 'FastFoodRestaurant', label: 'Takeaway' },
    { id: 'BarOrPub', label: 'Pub or bar' },
    { id: 'Brewery', label: 'Brewery' },
    { id: 'HairSalon', label: 'Hair salon or barber' },
    { id: 'BeautySalon', label: 'Beauty salon' },
    { id: 'DaySpa', label: 'Spa' },
    { id: 'HealthClub', label: 'Gym or fitness studio' },
    { id: 'Dentist', label: 'Dentist' },
    { id: 'Physiotherapy', label: 'Physiotherapy' },
    { id: 'VeterinaryCare', label: 'Vet' },
    { id: 'Plumber', label: 'Plumber' },
    { id: 'Electrician', label: 'Electrician' },
    { id: 'GeneralContractor', label: 'Builder' },
    { id: 'RoofingContractor', label: 'Roofer' },
    { id: 'HousePainter', label: 'Painter and decorator' },
    { id: 'HomeAndConstructionBusiness', label: 'Other trade' },
    { id: 'AutoRepair', label: 'Garage or car care' },
    { id: 'Florist', label: 'Florist' },
    { id: 'BookStore', label: 'Bookshop' },
    { id: 'ClothingStore', label: 'Clothes shop' },
    { id: 'GardenStore', label: 'Garden centre' },
    { id: 'Store', label: 'Other shop' },
    { id: 'BedAndBreakfast', label: 'B&B or guest house' },
    { id: 'Hotel', label: 'Hotel' },
    { id: 'LodgingBusiness', label: 'Holiday let' },
    { id: 'AccountingService', label: 'Accountant' },
    { id: 'LegalService', label: 'Solicitor' },
    { id: 'RealEstateAgent', label: 'Estate agent' },
    { id: 'ChildCare', label: 'Nursery or childcare' },
    { id: 'ProfessionalService', label: 'Other professional service' }
];
export const BUSINESS_COUNTRIES = [
    { id: 'GB', label: 'United Kingdom' },
    { id: 'GG', label: 'Guernsey' },
    { id: 'JE', label: 'Jersey' },
    { id: 'IM', label: 'Isle of Man' },
    { id: 'IE', label: 'Ireland' }
];
export const PRICE_RANGES = ['£', '££', '£££', '££££'];
const TIME = /^(?:[01]\d|2[0-3]):[0-5]\d$|^24:00$/;
const PHONE = /^\+?[\d\s()-]{6,24}$/;
const EMAIL = /^[^\s@<>"]{1,64}@[^\s@<>"]{1,120}\.[a-z]{2,24}$/i;
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u2028\u2029<>]/;
const TEXT_LIMITS = {
    name: 120,
    street: 160,
    town: 80,
    region: 80,
    postcode: 12,
    areaServed: 160
};
/** Cleans and checks business details. Empty fields are dropped; nothing left means `undefined`. */
export function normalizeBusiness(input) {
    const errors = [];
    if (input === undefined || input === null)
        return { value: undefined, errors };
    if (typeof input !== 'object' || Array.isArray(input)) {
        return { value: undefined, errors: [{ path: 'business', message: 'Business details must be an object.' }] };
    }
    const raw = input;
    const out = {};
    const text = (key) => (typeof raw[key] === 'string' ? raw[key].replace(/\s+/g, ' ').trim() : '');
    const type = text('type');
    if (type) {
        if (BUSINESS_TYPES.some((t) => t.id === type))
            out.type = type;
        else
            errors.push({ path: 'business.type', message: 'Choose a kind of business from the list.' });
    }
    for (const [key, max] of Object.entries(TEXT_LIMITS)) {
        const value = text(key);
        if (!value)
            continue;
        if (value.length > max || CONTROL.test(value))
            errors.push({ path: `business.${key}`, message: `Keep it under ${max} characters, without < or >.` });
        else
            out[key] = key === 'postcode' ? value.toUpperCase() : value;
    }
    const phone = text('phone');
    if (phone) {
        if (PHONE.test(phone))
            out.phone = phone;
        else
            errors.push({ path: 'business.phone', message: 'Use numbers, spaces and + only, e.g. 01481 123456.' });
    }
    const email = text('email');
    if (email) {
        if (EMAIL.test(email))
            out.email = email;
        else
            errors.push({ path: 'business.email', message: 'That email address does not look right.' });
    }
    const country = text('country');
    if (country) {
        if (BUSINESS_COUNTRIES.some((c) => c.id === country))
            out.country = country;
        else
            errors.push({ path: 'business.country', message: 'Choose a country from the list.' });
    }
    const priceRange = text('priceRange');
    if (priceRange) {
        if (PRICE_RANGES.includes(priceRange))
            out.priceRange = priceRange;
        else
            errors.push({ path: 'business.priceRange', message: 'Choose a price range from the list.' });
    }
    if (raw.hours !== undefined && raw.hours !== null) {
        if (typeof raw.hours !== 'object' || Array.isArray(raw.hours)) {
            errors.push({ path: 'business.hours', message: 'Opening hours must be an object.' });
        }
        else {
            const hours = {};
            for (const [day, times] of Object.entries(raw.hours)) {
                const t = times;
                if (!DAY_CODES.includes(day) || !t || typeof t !== 'object') {
                    errors.push({ path: `business.hours.${day}`, message: 'Unknown day.' });
                    continue;
                }
                if (typeof t.opens === 'string' && typeof t.closes === 'string' && TIME.test(t.opens) && TIME.test(t.closes)) {
                    hours[day] = { opens: t.opens, closes: t.closes };
                }
                else {
                    errors.push({ path: `business.hours.${day}`, message: 'Use times like 09:00 and 17:30.' });
                }
            }
            out.hours = hours;
        }
    }
    return { value: Object.keys(out).length > 0 ? out : undefined, errors };
}
/** Days with the same times are grouped, the way schema.org and search engines like them. */
export function groupOpeningHours(hours) {
    const groups = [];
    for (const day of DAY_CODES) {
        const t = hours[day];
        if (!t)
            continue;
        const same = groups.find((g) => g.opens === t.opens && g.closes === t.closes);
        if (same)
            same.days.push(day);
        else
            groups.push({ days: [day], opens: t.opens, closes: t.closes });
    }
    return groups;
}
/**
 * The LocalBusiness JSON-LD for a page, ready to sit inside <script type="application/ld+json">, or null when there is
 * nothing beyond a name to say (an empty LocalBusiness only adds noise).
 */
export function businessJsonLd(business, meta, options = {}) {
    if (!business)
        return null;
    const hasAddress = Boolean(business.street || business.town || business.postcode);
    const hasHours = business.hours !== undefined && Object.keys(business.hours).length > 0;
    if (!business.phone && !business.email && !hasAddress && !hasHours)
        return null;
    const data = {
        '@context': 'https://schema.org',
        '@type': business.type ?? 'LocalBusiness',
        name: business.name || meta.title
    };
    if (meta.description)
        data.description = meta.description;
    if (options.url)
        data.url = options.url;
    if (options.image)
        data.image = options.image;
    if (business.phone)
        data.telephone = business.phone;
    if (business.email)
        data.email = business.email;
    if (hasAddress) {
        data.address = {
            '@type': 'PostalAddress',
            ...(business.street ? { streetAddress: business.street } : {}),
            ...(business.town ? { addressLocality: business.town } : {}),
            ...(business.region ? { addressRegion: business.region } : {}),
            ...(business.postcode ? { postalCode: business.postcode } : {}),
            ...(business.country ? { addressCountry: business.country } : {})
        };
    }
    if (hasHours) {
        data.openingHoursSpecification = groupOpeningHours(business.hours).map((g) => ({
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: g.days.map((d) => DAY_NAMES[d]),
            opens: g.opens,
            closes: g.closes
        }));
    }
    if (business.priceRange)
        data.priceRange = business.priceRange;
    if (business.areaServed)
        data.areaServed = business.areaServed;
    // Safe inside a <script> element: no "</script>" or line separators can survive.
    return JSON.stringify(data).replace(/</g, '\\u003c').replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');
}
//# sourceMappingURL=business.js.map